import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "../../BillingAndPayments/components/TableCell";
import Alert from "@mui/material/Alert";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import {
  startListMcpConnections,
  startRevokeMcpConnection,
} from "../../../actions/mcpOAuth";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import Button from "../../../components/Buttons/Button";
import TableContainer from "../../../components/Tables/TableContainer";
import { DEFAULT_TABLE_PAGES } from "../../../utils/constants";
import { readMcpConnectionTime } from "../../../utils/mcpOAuth";
import {
  BUTTON_CANCEL,
  COLUMN_NAME_ACTION,
  COLUMN_NAME_DATE,
  COLUMN_NAME_NAME,
  COLUMN_NAME_STATUS,
  DATE_SHORT,
  BUTTON_RETRY,
  ERROR,
  MCP_AUTHORIZE_ADVERTISER_NOT_FOUND,
  MCP_AUTHORIZE_PERMISSION_ERROR,
  MCP_CONNECTIONS_DATE_UNKNOWN,
  MCP_CONNECTIONS_DESCRIPTION,
  MCP_CONNECTIONS_EMPTY,
  MCP_CONNECTIONS_LOAD_ERROR,
  MCP_CONNECTIONS_REVOKE,
  MCP_CONNECTIONS_REVOKE_CONFIRM_TEXT,
  MCP_CONNECTIONS_REVOKE_CONFIRM_TITLE,
  MCP_CONNECTIONS_REVOKE_ERROR,
  MCP_CONNECTIONS_STATUS_ACTIVE,
  MCP_CONNECTIONS_STATUS_REVOKED,
  MCP_CONNECTIONS_TABLE_ARIA,
  MCP_CONNECTIONS_TITLE,
  MCP_CONNECTIONS_UNKNOWN_CLIENT,
} from "../../../i18n/keysTranslations";

const messageKeyForKind = (kind) => {
  switch (kind) {
    case "permission":
      return MCP_AUTHORIZE_PERMISSION_ERROR;
    case "notFound":
      return MCP_AUTHORIZE_ADVERTISER_NOT_FOUND;
    default:
      return MCP_CONNECTIONS_LOAD_ERROR;
  }
};

const McpConnections = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const advertiserID = useSelectorApp(
    (state) => state.auth.user?.advertiserID || ""
  );
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadErrorKind, setLoadErrorKind] = useState(null);
  const [revokingKeyID, setRevokingKeyID] = useState("");
  const [page, setPage] = useState(0);

  const loadConnections = useCallback(async () => {
    if (!advertiserID) {
      setConnections([]);
      setLoadErrorKind("notFound");
      return;
    }

    setIsLoading(true);
    setLoadErrorKind(null);
    const result = await dispatch(startListMcpConnections({ advertiserID }));
    setIsLoading(false);

    if (!result?.ok) {
      setConnections([]);
      setLoadErrorKind(result?.errorKind || "unexpected");
      return;
    }

    setConnections(result.data || []);
    setPage(0);
  }, [advertiserID, dispatch]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const onRevoke = async ({ keyID, clientName }) => {
    const confirmed = await ConfirmAlert({
      title: t(MCP_CONNECTIONS_REVOKE_CONFIRM_TITLE),
      text: t(MCP_CONNECTIONS_REVOKE_CONFIRM_TEXT, {
        clientName: clientName || t(MCP_CONNECTIONS_UNKNOWN_CLIENT),
      }),
      confirmButtonText: t(MCP_CONNECTIONS_REVOKE),
      cancelButtonText: t(BUTTON_CANCEL),
    });
    if (!confirmed?.isConfirmed) return;

    setRevokingKeyID(keyID);
    const result = await dispatch(
      startRevokeMcpConnection({ keyID, advertiserID })
    );
    setRevokingKeyID("");

    if (!result?.ok) {
      const revokeErrorKey =
        result?.errorKind === "permission"
          ? MCP_AUTHORIZE_PERMISSION_ERROR
          : result?.errorKind === "notFound"
            ? MCP_AUTHORIZE_ADVERTISER_NOT_FOUND
            : MCP_CONNECTIONS_REVOKE_ERROR;
      SimpleAlert({
        title: t(ERROR),
        text: t(revokeErrorKey),
        icon: "error",
      });
      return;
    }

    await loadConnections();
  };

  const visibleConnections = connections.slice(
    page * DEFAULT_TABLE_PAGES,
    page * DEFAULT_TABLE_PAGES + DEFAULT_TABLE_PAGES
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t(MCP_CONNECTIONS_TITLE)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: "text.secondary", lineHeight: 1.5 }}
        >
          {t(MCP_CONNECTIONS_DESCRIPTION)}
        </Typography>
      </Box>

      {loadErrorKind && !isLoading ? (
        <Alert
          severity="error"
          action={
            <Button type="button" color="secondary" onClick={loadConnections}>
              {t(BUTTON_RETRY)}
            </Button>
          }
        >
          {t(messageKeyForKind(loadErrorKind))}
        </Alert>
      ) : (
        <TableContainer
          page={page}
          onPageChange={setPage}
          rowsPerPage={DEFAULT_TABLE_PAGES}
          keys={connections.map((connection) => connection.keyID)}
          loading={isLoading}
          ariaLabel={t(MCP_CONNECTIONS_TABLE_ARIA)}
          emptyText={MCP_CONNECTIONS_EMPTY}
          hasPagination={connections.length > DEFAULT_TABLE_PAGES}
        >
          <TableHead>
            <TableRow>
              <TableCell isHeader sx={{ pl: "16px !important" }}>
                {t(COLUMN_NAME_NAME)}
              </TableCell>
              <TableCell isHeader>{t(COLUMN_NAME_DATE)}</TableCell>
              <TableCell isHeader>{t(COLUMN_NAME_STATUS)}</TableCell>
              <TableCell isHeader>{t(COLUMN_NAME_ACTION)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleConnections.map((connection, index) => {
              const isLastRow = index === visibleConnections.length - 1;
              const time = readMcpConnectionTime(connection.creationTime);
              const clientName =
                connection.clientName || t(MCP_CONNECTIONS_UNKNOWN_CLIENT);
              return (
                <TableRow key={connection.keyID}>
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    sx={{ pl: "16px !important" }}
                  >
                    {clientName}
                  </TableCell>
                  <TableCell indexRow={index} isLastRow={isLastRow}>
                    {time > 0
                      ? t(DATE_SHORT, { date: new Date(time) })
                      : t(MCP_CONNECTIONS_DATE_UNKNOWN)}
                  </TableCell>
                  <TableCell indexRow={index} isLastRow={isLastRow}>
                    {t(
                      connection.isRevoked
                        ? MCP_CONNECTIONS_STATUS_REVOKED
                        : MCP_CONNECTIONS_STATUS_ACTIVE
                    )}
                  </TableCell>
                  <TableCell indexRow={index} isLastRow={isLastRow}>
                    {connection.isRevoked ? null : (
                      <Button
                        type="button"
                        color="secondary"
                        loading={revokingKeyID === connection.keyID}
                        disabled={Boolean(revokingKeyID)}
                        onClick={() =>
                          onRevoke({
                            keyID: connection.keyID,
                            clientName,
                          })
                        }
                      >
                        {t(MCP_CONNECTIONS_REVOKE)}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableContainer>
      )}
    </Box>
  );
};

export default McpConnections;
